<template>
  <div class="subscription-center">
    <div class="header-section">
      <h1 class="page-title">会员订阅中心</h1>
      <p class="subtitle">解锁高级量化分析功能，把握市场先机</p>
    </div>

    <!-- 今日名额已满提示 -->
    <div v-if="isDailyLimitReached" class="alert alert-warning border-warning text-warning mb-4 text-center py-3 rounded-lg mx-auto" style="max-width: 800px;">
      <b-icon icon="exclamation-triangle-fill" class="mr-2"></b-icon>
      今日名额已满，每天8点开放名额。
    </div>

    <div class="main-content">
      <div class="modules-section">
        <h2>选择需要订阅的模块</h2>
        <div class="modules-grid">
          <div 
            v-for="mod in modules" 
            :key="mod.code" 
            class="module-card"
            :class="{ active: selectedModules.includes(mod.code) }"
            @click="toggleModule(mod.code)"
          >
            <!-- 组合优惠角标 -->
            <div v-if="(mod.code === 'sentiment_vip' || mod.code === 'topic_kline')" class="bundle-badge">
              组合立减¥200
            </div>
            
            <div class="card-header">
              <div class="custom-checkbox">
                <span v-if="selectedModules.includes(mod.code)">✓</span>
              </div>
              <h3 class="module-name">{{ mod.name }}</h3>
            </div>
            <div class="module-desc">{{ mod.description }}</div>
            <div class="module-price">
              <span class="currency">¥</span>
              <span class="amount">{{ mod.price_per_month }}</span>
              <span class="unit">/月</span>
            </div>

            <!-- 使用说明链接 -->
            <div class="module-instruction mt-3" v-if="mod.code === 'sentiment_vip' || mod.code === 'topic_kline'">
              <a 
                v-if="mod.code === 'sentiment_vip'"
                href="https://mp.weixin.qq.com/s/WEntWXOSlTBLaMmwI-UTLA" 
                target="_blank" 
                class="small text-primary text-decoration-underline"
                @click.stop
              >
                <b-icon icon="question-circle" class="mr-1"></b-icon>情绪周期使用说明
              </a>
              <a 
                v-if="mod.code === 'topic_kline'"
                href="https://mp.weixin.qq.com/s/BV6xPZFO0O6-74lATXH5og" 
                target="_blank" 
                class="small text-primary text-decoration-underline"
                @click.stop
              >
                <b-icon icon="question-circle" class="mr-1"></b-icon>题材表格VIP使用说明
              </a>
            </div>
            <!-- 显示预计到期日期 -->
            <div v-if="selectedModules.includes(mod.code)" class="expected-date mt-2">
              <div class="small font-weight-bold" style="color: #e6a23c;">
                <b-icon icon="clock-history" class="mr-1"></b-icon>
                预计到期: {{ getExpectedEndDate(mod.code) }}
              </div>
            </div>
          </div>
        </div>
        
        <!-- 组合优惠提示条 -->
        <div v-if="selectedModules.length === 1 && (selectedModules.includes('sentiment_vip') || selectedModules.includes('topic_kline'))" 
             class="bundle-tip animate__animated animate__fadeIn">
          <b-icon icon="lightning-fill" class="mr-1"></b-icon>
          温馨提示：同时订阅“情绪周期VIP”与“题材表格VIP”，每月可额外立减 <strong>¥200</strong>！
        </div>
      </div>

      <div class="duration-section">
        <h2>选择订阅时长</h2>
        <div class="duration-options">
          <div 
            v-for="m in durationOptions" 
            :key="m.value"
            class="duration-card"
            :class="{ active: selectedMonths === m.value }"
            @click="selectedMonths = m.value"
          >
            <div class="duration-title">{{ m.label }}</div>
            <div class="duration-discount" v-if="m.value === 3">享 9 折优惠</div>
            <div class="duration-discount" v-if="m.value === 6">享 8.5 折优惠</div>
            <div class="duration-discount" v-if="m.value === 12">
               <b-icon icon="star-fill" class="mr-1"></b-icon>最划算：享 8 折优惠
            </div>
          </div>
        </div>
      </div>

      <div class="checkout-section" v-if="selectedModules.length > 0">
        <div class="summary-receipt mb-4">
          <div class="receipt-header">
            <h3 class="m-0">费用明细</h3>
            <span class="text-muted small">ID: {{ (Math.random()*1000000).toFixed(0) }}</span>
          </div>
          <div class="receipt-body">
            <div class="summary-item">
              <span>基础价格 ({{ selectedModules.length }}个模块 × {{ selectedMonths }}个月)</span>
              <span>¥{{ originalPrice.toFixed(2) }}</span>
            </div>
            <div class="summary-item text-danger" v-if="selectedMonths === 3">
              <span><b-icon icon="clock" class="mr-1"></b-icon>季度订阅 9 折优惠</span>
              <span>- ¥{{ (originalPrice * 0.1).toFixed(2) }}</span>
            </div>
            <div class="summary-item text-danger" v-if="selectedMonths === 6">
              <span><b-icon icon="clock-fill" class="mr-1"></b-icon>半年订阅 8.5 折优惠</span>
              <span>- ¥{{ (originalPrice * 0.15).toFixed(2) }}</span>
            </div>
            <div class="summary-item text-danger" v-if="selectedMonths >= 12">
              <span><b-icon icon="arrow-down-circle" class="mr-1"></b-icon>年度订阅 8 折优惠</span>
              <span>- ¥{{ (originalPrice * 0.2).toFixed(2) }}</span>
            </div>
            <div class="summary-item text-danger" v-if="selectedModules.length >= 3">
              <span><b-icon icon="layers" class="mr-1"></b-icon>多模块组合 8 折优惠</span>
              <span>- ¥{{ (originalPrice * 0.2).toFixed(2) }}</span>
            </div>
            <div class="summary-item text-danger" v-if="isBundleSelected">
              <span><b-icon icon="gift" class="mr-1"></b-icon>双模块深度特惠</span>
              <span>- ¥{{ (200 * selectedMonths).toFixed(2) }}</span>
            </div>
            <div class="summary-item text-danger" v-if="appliedCoupon">
              <span><b-icon icon="tag" class="mr-1"></b-icon>优惠券抵扣</span>
              <span>- ¥{{ appliedCoupon.amount.toFixed(2) }}</span>
            </div>
            <div class="summary-item text-danger" v-if="useReferralPoints && calculatePointsDeduction() > 0">
              <span><b-icon icon="gem" class="mr-1"></b-icon>分享积分抵扣</span>
              <span>- ¥{{ calculatePointsDeduction().toFixed(2) }}</span>
            </div>
            
            <div class="receipt-divider"></div>
            
            <div class="summary-item total">
              <span>应付总计</span>
              <div class="text-right">
                <div class="final-price">¥{{ finalPrice.toFixed(2) }}</div>
                <div v-if="originalPrice > finalPrice" class="saved-badge animate__animated animate__pulse animate__infinite">
                  已省 ¥{{ savedAmount }}
                </div>
              </div>
            </div>
          </div>
          <div class="receipt-footer">
            感谢支持我们的量化研究
          </div>
        </div>

        <!-- 优惠与抵扣区域 -->
        <div class="discount-wrapper mt-4">
          <!-- 优惠券卡片 -->
          <div class="discount-card coupon-card mb-3">
            <div class="d-flex align-items-center mb-3">
              <h3 class="m-0" style="font-size: 16px;">
                <b-icon icon="tag-fill" class="mr-2 text-primary"></b-icon>使用优惠券
              </h3>
            </div>
            <div class="coupon-input-box p-3 rounded border" :class="appliedCoupon ? 'bg-light-primary' : 'bg-light'">
              <div class="coupon-input-group">
                <b-form-input 
                  v-model="couponCode" 
                  placeholder="请输入优惠券码" 
                  class="coupon-input border-0 bg-transparent"
                  :disabled="!!appliedCoupon"
                  style="box-shadow: none;"
                ></b-form-input>
                <div class="divider mx-2"></div>
                <b-button 
                  variant="primary" 
                  size="sm"
                  @click="handleApplyCoupon" 
                  v-if="!appliedCoupon"
                  :disabled="!couponCode"
                  class="px-3 rounded-pill"
                >
                  应用
                </b-button>
                <b-button 
                  variant="outline-danger" 
                  size="sm"
                  v-else 
                  @click="removeCoupon" 
                  class="px-3 rounded-pill"
                >
                  取消
                </b-button>
              </div>
              <div v-if="appliedCoupon" class="mt-2 pt-2 border-top border-white-50 text-success small font-weight-bold d-flex align-items-center">
                <b-icon icon="check-circle-fill" class="mr-1"></b-icon>
                已成功抵扣 ¥{{ appliedCoupon.amount.toFixed(2) }}
              </div>
            </div>
          </div>

          <!-- 积分卡片 -->
          <div class="discount-card points-card">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h3 class="m-0" style="font-size: 16px;">
                <b-icon icon="gem" class="mr-2 text-warning"></b-icon>分享积分抵扣
              </h3>
              <b-link to="/me/profile?tab=3" class="small text-primary font-weight-bold">
                获取积分 <b-icon icon="arrow-right-short"></b-icon>
              </b-link>
            </div>
            
            <div class="points-control-box p-3 rounded border" :class="userReferralPoints > 0 ? (useReferralPoints ? 'bg-light-success' : 'bg-light-soft') : 'bg-light'">
              <div class="d-flex align-items-center justify-content-between">
                <div class="flex-grow-1">
                  <div class="d-flex align-items-baseline">
                    <span class="text-muted small mr-2">可用积分</span>
                    <span class="h4 mb-0 font-weight-bold" :class="userReferralPoints > 0 ? 'text-success' : 'text-muted'">{{ userReferralPoints }}</span>
                  </div>
                  <div class="small mt-1" :class="userReferralPoints > 0 ? 'text-dark' : 'text-muted'">
                    <span v-if="userReferralPoints > 0">1 积分 = 1 元，下单立享抵扣</span>
                    <b-link v-else to="/me/profile?tab=3" class="text-muted text-decoration-none hover-primary">
                      邀请好友注册即可获得积分奖励 <b-icon icon="chevron-right" font-scale="0.8"></b-icon>
                    </b-link>
                  </div>
                </div>
                <div class="pl-3">
                  <b-form-checkbox 
                    v-model="useReferralPoints" 
                    switch 
                    size="lg" 
                    :disabled="userReferralPoints <= 0"
                  ></b-form-checkbox>
                </div>
              </div>
              
              <div v-if="useReferralPoints && calculatePointsDeduction() > 0" class="mt-2 pt-2 border-top border-white-50">
                <div class="text-success small font-weight-bold d-flex align-items-center">
                  <b-icon icon="check-circle-fill" class="mr-1"></b-icon>
                  本次下单将抵扣 ¥{{ calculatePointsDeduction().toFixed(2) }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="payment-methods mt-5">
          <h2 class="section-title mb-4">支付方式</h2>
          <div class="method-options">
            <div 
              class="method-card-new" 
              :class="{ active: paymentMethod === 'wechat' }"
              @click="paymentMethod = 'wechat'"
            >
              <div class="method-info">
                <b-icon icon="wechat" class="method-icon wechat-color mr-2"></b-icon>
                <span>微信支付</span>
              </div>
              <div class="check-mark"><b-icon icon="check" v-if="paymentMethod === 'wechat'"></b-icon></div>
            </div>
            <div 
              class="method-card-new" 
              :class="{ active: paymentMethod === 'alipay' }"
              @click="paymentMethod = 'alipay'"
            >
              <div class="method-info">
                <b-icon icon="cursor-fill" class="method-icon alipay-color mr-2" rotate="45"></b-icon>
                <span>支付宝支付</span>
              </div>
              <div class="check-mark"><b-icon icon="check" v-if="paymentMethod === 'alipay'"></b-icon></div>
            </div>
          </div>
        </div>

        <div class="action-btn">
          <b-button variant="primary" size="lg" class="pay-btn px-5" @click="handleSubscribe" :disabled="loading || isDailyLimitReached">
            <b-spinner small v-if="loading" class="mr-2"></b-spinner>
            立即支付 ¥{{ finalPrice.toFixed(2) }}
          </b-button>
        </div>
      </div>
    </div>

    <!-- 支付二维码弹窗 -->
    <b-modal
      v-model="qrDialogVisible"
      title="扫码支付"
      centered
      hide-footer
      no-close-on-backdrop
      @hidden="stopPolling"
    >
      <div class="qr-container text-center py-3">
        <div class="qr-tips mb-3">
          {{ paymentMethod === 'wechat' ? '请使用微信扫一扫完成支付' : '请使用支付宝扫码完成支付' }}
        </div>
        
        <div v-if="qrLoading" class="py-5">
          <b-spinner variant="primary"></b-spinner>
          <div class="mt-2 text-muted">正在连接支付网关...</div>
        </div>
        <div v-else-if="!qrCodeDataUrl" class="qr-placeholder py-5 bg-light rounded mb-3">
          正在生成二维码...
        </div>
        <div v-else class="qr-image-wrapper mb-3">
          <img :src="qrCodeDataUrl" style="width: 220px; height: 220px; border: 1px solid #eee; padding: 10px;" />
        </div>
        
        <div class="qr-price mb-4">
          <span class="text-muted mr-1">支付金额:</span>
          <span class="amount text-danger h3 font-weight-bold">¥ {{ finalPrice.toFixed(2) }}</span>
        </div>
        
        <div class="d-flex justify-content-center pt-2">
          <b-button variant="outline-secondary" class="mr-3" @click="qrDialogVisible = false">取消支付</b-button>
          <b-button variant="primary" @click="checkPaymentStatus" :disabled="checking">
            <b-spinner small v-if="checking" class="mr-1"></b-spinner>
            我已完成支付
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- 支付成功弹窗 -->
    <b-modal v-model="success" title="支付成功" centered hide-footer no-close-on-backdrop>
      <div class="text-center py-4">
        <b-icon icon="check-circle-fill" variant="success" font-scale="4" class="mb-3"></b-icon>
        <h4 style="color: var(--text-main);">感谢您的订阅！</h4>
        <p class="text-muted">您的权限已实时开通，请前往个人中心查看。</p>
        <div class="mt-4">
          <b-button variant="primary" to="/me/profile?tab=1" block>前往我的订阅</b-button>
          <b-button variant="outline-secondary" class="mt-2" @click="success = false" block>留在本页</b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { request_zzquant } from 'network/request.js'
import QRCode from 'qrcode'

export default {
  name: 'SubscriptionCenter',
  data() {
    return {
      modules: [],
      selectedModules: [],
      selectedMonths: 1,
      durationOptions: [
        { label: '1个月', value: 1 },
        { label: '3个月', value: 3 },
        { label: '6个月', value: 6 },
        { label: '12个月', value: 12 }
      ],
      paymentMethod: 'wechat',
      loading: false,
      qrDialogVisible: false,
      qrLoading: false,
      qrCodeDataUrl: '',
      currentOrderNo: '',
      pollingInterval: null,
      checking: false,
      success: false, // 是否支付成功返回
      userSubscriptions: [], // 存储用户当前订阅信息
      couponCode: '',
      appliedCoupon: null,
      isDailyLimitReached: false,
      userReferralPoints: 0,
      useReferralPoints: false
    }
  },
  computed: {
    originalPrice() {
      if (!this.modules || !this.selectedModules || this.selectedModules.length === 0) return 0
      let base = 0
      this.selectedModules.forEach(code => {
        const mod = this.modules.find(m => m && m.code === code)
        if (mod && mod.price_per_month) base += Number(mod.price_per_month)
      })
      return base * (this.selectedMonths || 1)
    },
    finalPrice() {
      let discount = 1.0
      if (this.selectedMonths === 3) discount = 0.9
      else if (this.selectedMonths === 6) discount = 0.85
      else if (this.selectedMonths >= 12) discount = 0.8
      
      if (this.selectedModules.length >= 3) discount *= 0.8
      
      let price = this.originalPrice * discount
      
      // 只有“情绪周期VIP”和“题材表格VIP”组合才会额外每月减去200
      if (this.isBundleSelected) {
        price -= 200 * this.selectedMonths
      }
      
      // 扣除优惠券
      if (this.appliedCoupon) {
        price -= this.appliedCoupon.amount
      }

      // 扣除分享积分抵扣
      if (this.useReferralPoints) {
        price -= this.calculatePointsDeduction()
      }
      
      return Math.max(0.01, price)
    },
    isBundleSelected() {
      if (this.selectedModules.length !== 2) return false
      const bundle = ['sentiment_vip', 'topic_kline']
      return bundle.every(code => this.selectedModules.includes(code))
    },
    savedAmount() {
      return (this.originalPrice - this.finalPrice).toFixed(2)
    },
    discountRate() {
      if (this.originalPrice <= 0) return 10
      const rate = (this.finalPrice / this.originalPrice) * 10
      return rate.toFixed(1)
    }
  },
  created() {
    this.fetchModules()
    this.fetchUserSubscriptions()
    this.checkPaymentResult()
    this.fetchSettings()
    this.fetchReferralStats()
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    async fetchReferralStats() {
      try {
        const res = await request_zzquant({ url: '/v3/user/referral/stats', method: 'get' })
        if (res.data && res.data.code === 20000 && res.data.data) {
          this.userReferralPoints = res.data.data.total_points || 0
        }
      } catch (error) {
        console.error('获取推荐积分失败', error)
      }
    },
    calculatePointsDeduction() {
      // 计算积分抵扣：当前订单金额最多能被积分抵扣多少（1积分=1元）
      // 优惠后的金额如果不使用积分是：
      let discount = 1.0
      if (this.selectedMonths === 3) discount = 0.9
      else if (this.selectedMonths === 6) discount = 0.85
      else if (this.selectedMonths >= 12) discount = 0.8
      
      if (this.selectedModules.length >= 3) discount *= 0.8
      let price = this.originalPrice * discount
      if (this.isBundleSelected) price -= 200 * this.selectedMonths
      if (this.appliedCoupon) price -= this.appliedCoupon.amount
      
      const priceBeforePoints = Math.max(0.01, price)
      // 最多抵扣到订单金额剩下 0.01 元
      const maxDeductible = Math.max(0, priceBeforePoints - 0.01)
      return Math.min(this.userReferralPoints, maxDeductible)
    },
    async fetchModules() {
      try {
        const res = await request_zzquant({
          url: '/v3/payment/modules',
          method: 'get'
        })
        if (res.data && res.data.code === 20000) {
          this.modules = res.data.data
          
          // 自动选中从 URL query 传来的模块
          const queryModule = this.$route.query.module
          if (queryModule && !this.selectedModules.includes(queryModule)) {
            const modExists = this.modules.some(m => m.code === queryModule)
            if (modExists) {
              this.selectedModules = [queryModule] // 使用赋值确保响应性
            }
          }
        }
      } catch (error) {
        console.error('获取模块失败', error)
      }
    },
    async fetchUserSubscriptions() {
      try {
        const res = await request_zzquant({
          url: '/v3/user/subscriptions',
          method: 'get'
        })
        if (res.data && res.data.code === 20000) {
          this.userSubscriptions = res.data.data.feature_subscriptions || []
        }
      } catch (error) {
        console.error('获取用户订阅失败', error)
      }
    },
    checkPaymentResult() {
      try {
        const query = this.$route.query
        // 只要有 out_trade_no 且页面没报错，就尝试显示成功
        if (query && query.out_trade_no && query.trade_no) {
          this.success = true
        }
      } catch (e) {
        console.error('Check payment result error:', e)
      }
    },
    getExpectedEndDate(code) {
      if (!this.userSubscriptions) return '-'
      const sub = this.userSubscriptions.find(s => s.code === code)
      let baseDate = new Date()
      
      if (sub && sub.end) {
        // 后端返回格式 "YYYY-MM-DD HH:mm:ss"，某些浏览器需要兼容处理
        const endStr = sub.end.replace(/-/g, '/')
        const existingEnd = new Date(endStr)
        // 如果现有订阅未到期，则从到期时间开始顺延
        if (existingEnd > baseDate) {
          baseDate = existingEnd
        }
      }
      
      // 计算 N 个月后的日期
      const futureDate = new Date(baseDate)
      futureDate.setMonth(futureDate.getMonth() + this.selectedMonths)
      
      // 格式化为 YYYY-MM-DD 23:59:59
      const y = futureDate.getFullYear()
      const m = String(futureDate.getMonth() + 1).padStart(2, '0')
      const d = String(futureDate.getDate()).padStart(2, '0')
      
      return `${y}-${m}-${d} 23:59:59`
    },
    async handleApplyCoupon() {
      if (!this.couponCode) return
      const cleanCode = this.couponCode.trim()
      if (!cleanCode) return
      
      try {
        const res = await request_zzquant({
          url: '/v3/payment/coupon/validate',
          method: 'post',
          data: { code: cleanCode }
        })
        if (res.data && res.data.code === 20000) {
          this.appliedCoupon = res.data.data
          this.$bvToast.toast('优惠券应用成功', { variant: 'success', solid: true })
        } else {
          this.$bvToast.toast(res.data.message || '优惠券无效', { variant: 'danger', solid: true })
        }
      } catch (error) {
        this.$bvToast.toast('校验优惠券失败', { variant: 'danger', solid: true })
      }
    },
    removeCoupon() {
      this.appliedCoupon = null
      this.couponCode = ''
    },
    async fetchSettings() {
      try {
        const res = await request_zzquant({
          url: '/v3/payment/settings',
          method: 'get'
        })
        if (res.data && res.data.code === 20000) {
          this.isDailyLimitReached = res.data.data.is_daily_limit_reached
        }
      } catch (error) {
        console.error('获取设置失败', error)
      }
    },
    toggleModule(code) {
      const idx = this.selectedModules.indexOf(code)
      if (idx > -1) {
        this.selectedModules.splice(idx, 1)
      } else {
        this.selectedModules.push(code)
      }
    },
    async handleSubscribe() {
      if (this.selectedModules.length === 0) {
        this.$bvToast.toast('请至少选择一个模块', { variant: 'warning', solid: true })
        return
      }
      this.loading = true
      this.qrCodeDataUrl = ''
      
      try {
        const res = await request_zzquant({
          url: '/v3/payment/order/create',
          method: 'post',
          data: {
            module_codes: this.selectedModules,
            months: this.selectedMonths,
            payment_method: this.paymentMethod,
            coupon_code: this.appliedCoupon ? this.appliedCoupon.code.trim() : null,
            use_referral_points: this.useReferralPoints
          }
        })
        
        if (res.data && res.data.code === 20000) {
          this.currentOrderNo = res.data.data.order_no
          const pay_url = res.data.data.pay_url
          
          if (this.paymentMethod === 'wechat') {
            this.qrDialogVisible = true
            this.qrLoading = true
            
            // Ensure QRCode works
            try {
              const url = await QRCode.toDataURL(pay_url)
              this.qrCodeDataUrl = url
              this.startPolling()
            } catch (err) {
              console.error('生成二维码失败', err)
              this.$bvToast.toast('生成二维码失败', { variant: 'danger', solid: true })
            } finally {
              this.qrLoading = false
            }
          } else if (this.paymentMethod === 'alipay') {
            // For Alipay PC, usually redirect to the pay_url
            if (pay_url.startsWith('http')) {
              window.location.href = pay_url
            } else {
              // If it's a code_url for alipay native
              this.qrDialogVisible = true
              this.qrLoading = true
              try {
                const url = await QRCode.toDataURL(pay_url)
                this.qrCodeDataUrl = url
                this.startPolling()
              } catch (err) {
                this.$bvToast.toast('生成二维码失败', { variant: 'danger', solid: true })
              } finally {
                this.qrLoading = false
              }
            }
          }
        } else {
          this.$bvToast.toast((res.data && res.data.message) || '创建订单失败', { variant: 'danger', solid: true })
        }
      } catch (error) {
        console.error('支付请求失败', error)
        this.$bvToast.toast('服务请求异常', { variant: 'danger', solid: true })
      } finally {
        this.loading = false
      }
    },
    startPolling() {
      this.stopPolling()
      this.pollingInterval = setInterval(() => {
        this.checkPaymentStatus(false)
      }, 3000)
    },
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval)
        this.pollingInterval = null
      }
    },
    async checkPaymentStatus(manual = true) {
      if (!this.currentOrderNo) return
      if (manual) this.checking = true
      
      try {
        const res = await request_zzquant({
          url: `/v3/payment/order/${this.currentOrderNo}/status`,
          method: 'get'
        })
        if (res.data && res.data.code === 20000 && res.data.data.status === 'paid') {
          this.stopPolling()
          this.qrDialogVisible = false
          this.$bvToast.toast('支付成功，权益已生效！', { variant: 'success', solid: true })
          setTimeout(() => {
            this.$router.push('/me/profile?tab=1')
          }, 1500)
        } else if (manual) {
          this.$bvToast.toast('尚未收到支付成功通知', { variant: 'info', solid: true })
        }
      } catch (error) {
        console.error('查询订单状态失败', error)
      } finally {
        if (manual) this.checking = false
      }
    }
  }
}
</script>

<style scoped>
.subscription-center {
  max-width: 1000px;
  margin: 0 auto;
  padding: 40px 20px;
}

.header-section {
  text-align: center;
  margin-bottom: 50px;
}

.page-title {
  font-size: 32px;
  color: var(--text-main);
  margin-bottom: 16px;
}

.subtitle {
  font-size: 16px;
  color: var(--text-muted);
}

.main-content {
  background: var(--bg-card);
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  border: 1px solid var(--border-color);
}

h2 {
  font-size: 20px;
  margin-bottom: 24px;
  color: var(--text-main);
}

.modules-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
  margin-bottom: 40px;
}

.module-card {
  border: 2px solid var(--border-color);
  border-radius: 8px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s;
  position: relative;
  background-color: var(--bg-card);
}

.module-card:hover {
  border-color: var(--accent-color);
}

.module-card.active {
  border-color: var(--accent-color) !important;
  background-color: var(--bg-muted) !important;
}

.card-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  background: transparent !important;
  border-bottom: none !important;
  padding: 0 !important;
}

.custom-checkbox {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1px solid var(--border-color);
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: transparent;
  font-size: 12px;
}

.module-card.active .custom-checkbox {
  background-color: var(--accent-color);
  border-color: var(--accent-color);
  color: #fff;
}

.module-name {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: var(--text-main);
}

.module-desc {
  font-size: 13px;
  color: var(--text-muted);
  margin-bottom: 20px;
  min-height: 40px;
}

.expected-date {
  padding-top: 10px;
  border-top: 1px dashed var(--border-color);
}

.module-price {
  color: #f56c6c;
  font-weight: 600;
}

.module-price .amount {
  font-size: 24px;
}

.duration-options {
  display: flex;
  gap: 16px;
  margin-bottom: 40px;
}

.duration-card {
  flex: 1;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 16px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  background-color: var(--bg-card);
  color: var(--text-main);
}

.duration-card.active {
  border-color: var(--accent-color) !important;
  background-color: var(--bg-muted) !important;
  color: var(--accent-color);
  font-weight: bold;
}

.duration-discount {
  font-size: 12px;
  color: #f56c6c;
  margin-top: 8px;
  background: rgba(245, 108, 108, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
  display: inline-block;
}

.checkout-section {
  border-top: 1px solid var(--border-color);
  padding-top: 30px;
}

.summary {
  background: var(--bg-muted) !important;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 30px;
  border: 1px solid var(--border-color);
}

.summary-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  font-size: 15px;
  color: var(--text-main);
}

.summary-item.total {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px dashed var(--border-color);
  font-size: 18px;
  font-weight: bold;
  align-items: center;
}

.highlight {
  color: #f56c6c;
}

.price-box {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.original-price {
  font-size: 14px;
  color: var(--text-muted);
  text-decoration: line-through;
  font-weight: normal;
}

.final-price {
  font-size: 28px;
  color: #f56c6c;
  font-weight: bold;
}

.saved-amount {
  font-size: 13px;
  color: #f56c6c;
  background: rgba(245, 108, 108, 0.1);
  padding: 2px 8px;
  border-radius: 4px;
}

.discount-badge {
  font-size: 16px;
  color: #f56c6c;
  font-weight: bold;
}

.coupon-section {
  margin-bottom: 30px;
}

.coupon-section h3 {
  font-size: 16px;
  margin-bottom: 12px;
  color: var(--text-main);
}

.coupon-input-group {
  display: flex;
  gap: 10px;
  max-width: 460px;
  align-items: center;
}

.coupon-input {
  flex: 1;
  background-color: var(--bg-card) !important;
  border-color: var(--border-color) !important;
  color: var(--text-main) !important;
  height: 42px;
}

.coupon-section .btn {
  white-space: nowrap;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 80px;
}

.success-color {
  color: #67c23a;
  font-size: 13px;
}

.method-options {
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
}

.method-card {
  border: 1px solid var(--border-color);
  padding: 12px 24px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  transition: all 0.3s;
  background-color: var(--bg-card);
  color: var(--text-main);
}

.method-card.active {
  border-color: var(--accent-color) !important;
  background-color: var(--bg-muted) !important;
}

.method-icon {
  font-size: 20px;
}

.wechat-color { color: #07c160; }
.alipay-color { color: #1677ff; }

.action-btn {
  text-align: right;
}

.pay-btn {
  font-size: 18px;
  font-weight: 600;
}

/* Points & Coupon Card Styles */
.discount-card {
  border-radius: 12px;
}

.coupon-input-box, .points-control-box {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.coupon-input-group {
  display: flex;
  align-items: center;
  max-width: 100%;
}

.divider {
  width: 1px;
  height: 20px;
  background-color: rgba(0, 0, 0, 0.1);
}

.bg-light-primary {
  background-color: rgba(0, 123, 255, 0.05) !important;
  border-color: rgba(0, 123, 255, 0.15) !important;
}

.bg-light-success {
  background-color: rgba(40, 167, 69, 0.08) !important;
  border-color: rgba(40, 167, 69, 0.2) !important;
}

.bg-light-soft {
  background-color: rgba(0, 0, 0, 0.02) !important;
}

.bg-light {
  background-color: var(--bg-muted) !important;
  border-color: var(--border-color) !important;
}

.border-white-50 {
  border-color: rgba(255, 255, 255, 0.5) !important;
}

.hover-primary:hover {
  color: var(--accent-color) !important;
}

/* Payment Methods New Styles */
.method-card-new {
  flex: 1;
  border: 2px solid var(--border-color);
  padding: 16px 20px;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.25s ease;
  background-color: var(--bg-card);
  color: var(--text-main);
  position: relative;
}

.method-card-new:hover {
  border-color: var(--accent-color);
  transform: translateY(-2px);
}

.method-card-new.active {
  border-color: var(--accent-color);
  background-color: rgba(0, 123, 255, 0.03);
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.1);
}

.method-info {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 500;
}

.method-icon {
  font-size: 24px;
}

.check-mark {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  color: #fff;
  font-size: 14px;
}

.method-card-new.active .check-mark {
  background-color: var(--accent-color);
  border-color: var(--accent-color);
}

/* Receipt Styles */
.summary-receipt {
  background-color: var(--bg-muted);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid var(--border-color);
  box-shadow: 0 4px 15px rgba(0,0,0,0.05);
}

.receipt-header {
  padding: 15px 20px;
  background-color: rgba(0,0,0,0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px dashed var(--border-color);
}

.receipt-body {
  padding: 20px;
}

.receipt-divider {
  height: 1px;
  border-top: 1px dashed var(--border-color);
  margin: 15px 0;
}

.receipt-footer {
  padding: 10px;
  text-align: center;
  font-size: 12px;
  color: var(--text-muted);
  background-color: rgba(0,0,0,0.02);
  letter-spacing: 1px;
}

.saved-badge {
  display: inline-block;
  background: linear-gradient(135deg, #f56c6c 0%, #ff4949 100%);
  color: white;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: bold;
  margin-top: 5px;
}

.bundle-badge {
  position: absolute;
  top: -10px;
  right: -10px;
  background: #e6a23c;
  color: white;
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 4px;
  font-weight: bold;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(230, 162, 60, 0.4);
}

.bundle-tip {
  background-color: rgba(230, 162, 60, 0.1);
  border: 1px solid rgba(230, 162, 60, 0.2);
  color: #e6a23c;
  padding: 12px 20px;
  border-radius: 8px;
  margin-bottom: 30px;
  font-size: 14px;
}

/* Animations */
.animate__animated {
  animation-duration: 0.8s;
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

.animate__pulse {
  animation-name: pulse;
}

.animate__infinite {
  animation-iteration-count: infinite;
}

/* Mobile Adaptation */
@media (max-width: 768px) {
  .subscription-center {
    padding: 20px 10px;
  }
  
  .header-section {
    margin-bottom: 30px;
  }
  
  .page-title {
    font-size: 24px;
  }
  
  .subtitle {
    font-size: 14px;
  }
  
  .main-content {
    padding: 20px 15px;
  }
  
  h2 {
    font-size: 18px;
    margin-bottom: 16px;
  }
  
  .modules-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .duration-options {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
  
  .duration-card {
    padding: 12px 8px;
  }
  
  .duration-title {
    font-size: 14px;
  }
  
  .summary {
    padding: 15px;
  }
  
  .summary-item {
    font-size: 14px;
  }
  
  .summary-item.total {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .price-box {
    width: 100%;
    justify-content: space-between;
    align-items: center;
  }
  
  .final-price {
    font-size: 24px;
  }
  
  .coupon-input-group {
    flex-direction: column;
    align-items: stretch;
    max-width: 100%;
  }
  
  .coupon-section .btn {
    width: 100%;
  }
  
  .method-options {
    flex-direction: column;
    gap: 10px;
  }
  
  .method-card {
    padding: 12px;
    justify-content: center;
  }
  
  .action-btn {
    text-align: center;
  }
  
  .pay-btn {
    width: 100%;
    padding: 12px 0 !important;
  }
}
</style>
